import axios from 'axios';
import PropTypes from 'prop-types';
import React from 'react';
import { Consumer as PageConsumer } from '../../../contexts/PageContext';
import { EmitMetric } from '../../Analytics/VisibilityPixel/VisibilityPixel';
import { APIS } from '../../CallToAction/CommunityUserCallToActions/constants';
import CommunityInviteButton from '../../Community/CommunityInviteButton/CommunityInviteButton';
import Button from '../../Input/Button/Button';

import './Recommendations.scss';
import UserImage from '../../Content/User/UserDisplay/UserImage/UserImage';

export class Recommendations extends React.Component {
    constructor() {
        super();
        this.state = {
            recommendations: [],
        };
    }

    static propTypes = {
        /**
         * logged in user id passed from session id in page context
         */
        currentUserId: PropTypes.number.isRequired,
        /**
         * Boolean returned by a function, tells if user is an admin of at least one group
         */
        isGroupAdmin: PropTypes.bool,
    };

    static defaultProps = {
        currentUserId: null,
    };

    async componentDidMount() {
        try {
            const results = await axios.get('/api/recommendation/connections');
            this.setState({ recommendations: results.data });
        } catch (error) {
            throw new Error(error);
        }
    }

    followUser = (userIdToFollow) => async () => {
        const apiUrl = APIS.API_FOLLOW_USER;

        try {
            await axios.post(apiUrl, { userIdToFollow });
            this.setToFollowing(userIdToFollow);
            EmitMetric({
                misc_event_count: 1,
                misc_event_type: 'user-follow-recommended-connection',
            });
        } catch (error) {
            throw new Error(error);
        }
    };

    connectWithUser = async (userIdToConnect) => {
        const data = {
            toUserId: userIdToConnect,
            fromUsername: this.props.currentUserId,
            message: 'Invitation to Connect',
            isAnon: false,
        };

        try {
            await axios.post('/api/user/connection/request', data);
            EmitMetric({
                misc_event_count: 1,
                misc_event_type: 'user-request-recommended-connection',
            });
            this.setConnectionStatusToRequested(userIdToConnect);
        } catch (error) {
            throw new Error(error);
        }
    };

    setConnectionStatusToRequested = (userIdToConnect) => {
        const updatedRecommendations = [...this.state.recommendations].map(
            (recommendation) => {
                if (recommendation.user.userId === userIdToConnect) {
                    const recommendationCopy = { ...recommendation };
                    recommendationCopy.user.relation.connectionStatus =
                        'requested';
                    return recommendationCopy;
                }

                return recommendation;
            }
        );

        this.setState({ recommendations: updatedRecommendations });
    };

    setToFollowing = (userIdToFollow) => {
        const updatedRecommendations = [...this.state.recommendations].map(
            (recommendation) => {
                if (recommendation.user.userId === userIdToFollow) {
                    const recommendationCopy = { ...recommendation };
                    recommendationCopy.user.relation.isFollowing = true;
                    return recommendationCopy;
                }

                return recommendation;
            }
        );

        this.setState({ recommendations: updatedRecommendations });
    };

    renderButtons(isFollowing, connectionStatus, user) {
        const output = [];
        if (this.props.isGroupAdmin) {
            output.push(
                <CommunityInviteButton
                    key={`${user.username}-group-btn`}
                    inviteType={'group'}
                    buttonClass={'group-invite-button'}
                    buttonType={'wired-blue'}
                    value={'Invite to group'}
                    user={user}
                    callToActionHash={'invite-to-group'}
                    loginTrigger={'invite-to-group'}
                    metricEventType="invite-to-group-click-recommendations"
                />
            );
        } else {
            let buttonText = 'Follow';
            let disabled = false;
            let buttonType = 'wired-blue';
            let onClick = this.followUser(user.userId);
            let buttonClass = 'follow-user-button not-following';
            if (user && user.relation.isFollowing) {
                buttonType = 'primary-blue-gray';
                buttonText = 'Following';
                disabled = true;
                buttonClass = 'follow-user-button following';
                onClick = () => {};
            }

            output.push(
                <Button
                    key={`${user.username}-follow-btn`}
                    className={buttonClass}
                    type={buttonType}
                    value={buttonText}
                    onClick={onClick}
                    disabled={disabled}
                />
            );
        }

        const buttonProps = this.getNetworkButtonInfo(user);

        output.push(
            <CommunityInviteButton
                key={`${user.username}-connect-btn`}
                inviteType={'connection'}
                user={user}
                {...buttonProps}
            />
        );

        return output;
    }

    // Connect or Message Button Logic
    //==================================================================================
    getNetworkButtonInfo = (user) => {
        let value;
        let buttonType;
        let disabled;
        let className;
        let onClick = null;
        let onSuccess;
        switch (user && user.relation && user.relation.connectionStatus) {
            case 'requested':
                value = 'Requested';
                buttonType = 'primary-blue-gray';
                disabled = true;
                className = 'connect-user-button requested-button';
                break;
            case 'invited':
                value = 'Invited';
                buttonType = 'primary-blue-gray';
                className = 'connect-user-button invited-button';
                disabled = true;
                break;
            default:
                value = 'Connect';
                buttonType = 'secondary-blue-gray';
                disabled = false;
                className = 'connect-user-button connect-button';
                onSuccess = () =>
                    this.setConnectionStatusToRequested(user.userId);
        }

        return {
            className,
            value,
            onClick,
            buttonType,
            callToActionHash: 'connect-user',
            loginTrigger: 'connect-user',
            disabled,
            onSuccess,
        };
    };

    renderLegacyButtons(isFollowing, connectionStatus, userId) {
        return (
            <React.Fragment>
                <span
                    className={isFollowing ? 'follow requested' : 'follow'}
                    onClick={this.followUser(userId)}
                >
                    {isFollowing ? 'Following' : 'Follow'}
                </span>
                {connectionStatus === 'requested' && (
                    <span className="requested">Requested</span>
                )}
                {!connectionStatus && (
                    <span
                        className="connect"
                        onClick={() => this.connectWithUser(userId)}
                    >
                        Connect
                    </span>
                )}
            </React.Fragment>
        );
    }

    renderRecommendedConnection = ({ user, type }) => {
        const {
            username,
            credential,
            publicUrl,
            image,
            relation: { isFollowing, connectionStatus },
        } = user;

        return (
            <li key={username} className="recommendation">
                <a href={publicUrl}>
                    <UserImage image={image} iconSize={36} />
                </a>
                <div className="info-cta">
                    <div className="user-info">
                        <span className="username">
                            <a href={publicUrl}>{username}</a>
                        </span>
                        <span className="credential">{credential}</span>
                        <span className="connection-type">
                            {type === 'interests'
                                ? '2nd Degree Connection'
                                : 'FGB VIP'}
                        </span>
                    </div>

                    <div className={'cta-container'}>
                        {this.renderButtons(
                            isFollowing,
                            connectionStatus,
                            user
                        )}
                    </div>
                </div>
            </li>
        );
    };

    render() {
        if (this.state.recommendations.length === 0) {
            return <div></div>;
        }
        return (
            <section className="component-Messages-Recommendations">
                <h2 className="heading">Recommended Connections For You</h2>
                <ul>
                    {this.state.recommendations.map(
                        this.renderRecommendedConnection
                    )}
                </ul>
            </section>
        );
    }
}

export default (props) => (
    <PageConsumer>
        {(pageContext) => {
            const {
                pageProps,
                groups: { isGroupAdmin },
                pageProps: {
                    session: { id },
                },
            } = pageContext;
            return (
                <Recommendations
                    {...props}
                    currentUserId={id}
                    isGroupAdmin={isGroupAdmin()}
                />
            );
        }}
    </PageConsumer>
);
